import { scriptLanguage } from '@ly360/i18n';
import { IconButton } from '@ly360/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Languages, Undo2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';

/** Inbox translation between Arabic and English (shown only where the deployment has it set up). */
type Lang = 'ar' | 'en';
const langOf = (text: string): Lang => (scriptLanguage(text) === 'ar' ? 'ar' : 'en');
const other = (l: Lang): Lang => (l === 'ar' ? 'en' : 'ar');

export function useCanTranslate() {
  return (
    useQuery({
      queryKey: ['translate-status'],
      queryFn: () => api<{ available: boolean }>('/v1/translate/status'),
      staleTime: Infinity,
    }).data?.available ?? false
  );
}

/** "Translate" under a message: into my language, or into the other one when it is already in mine. */
export function MessageTranslation({ messageId, text }: { messageId: string; text: string }) {
  const { t, i18n } = useTranslation();
  const [shown, setShown] = useState(false);
  const mine: Lang = i18n.language === 'ar' ? 'ar' : 'en';
  const from = langOf(text);
  const to = from === mine ? other(mine) : mine;
  const run = useMutation({
    mutationFn: () =>
      api<{ text: string; from: Lang }>(`/v1/messages/${messageId}/translate`, {
        method: 'POST',
        body: JSON.stringify({ to }),
      }),
    onSuccess: () => setShown(true),
  });
  if (shown && run.data)
    return (
      <div className="msg-translation" data-testid="message-translation">
        <div className="msg-translation-label">
          <Languages size={12} aria-hidden="true" />
          {t('inbox.translate.from', { language: t(`inbox.translate.lang.${run.data.from}`) })}
          <button type="button" className="link-btn" onClick={() => setShown(false)}>
            {t('inbox.translate.hide')}
          </button>
        </div>
        <div dir="auto" lang={to}>
          {run.data.text}
        </div>
      </div>
    );
  const label = run.isPending
    ? t('inbox.translate.working')
    : run.isError
      ? t('inbox.translate.failed')
      : t('inbox.translate.to', { language: t(`inbox.translate.lang.${to}`) });
  return (
    <button
      type="button"
      className="msg-translate-btn tip tip-start"
      data-tip={label}
      aria-label={label}
      onClick={() => (run.data ? setShown(true) : run.mutate())}
      disabled={run.isPending}
      data-testid="translate-message"
    >
      <Languages size={12} aria-hidden="true" />
      {run.isPending ? t('inbox.translate.working') : t('inbox.translate.short')}
    </button>
  );
}

/** In the reply box: write in your language, send in the customer's (the original can be restored). */
export function ComposerTranslate({ text, onChange }: { text: string; onChange: (v: string) => void }) {
  const { t } = useTranslation();
  const [original, setOriginal] = useState<string | null>(null);
  const to = other(langOf(text));
  const run = useMutation({
    mutationFn: () =>
      api<{ text: string }>('/v1/translate', { method: 'POST', body: JSON.stringify({ text, to }) }),
    onSuccess: (r) => {
      setOriginal(text);
      onChange(r.text);
    },
  });
  if (original !== null)
    return (
      <IconButton
        icon={Undo2}
        label={t('inbox.translate.undo')}
        side="top"
        active
        onClick={() => {
          onChange(original);
          setOriginal(null);
        }}
        data-testid="translate-undo"
      />
    );
  return (
    <IconButton
      icon={Languages}
      label={
        run.isPending
          ? t('inbox.translate.working')
          : run.isError
            ? t('inbox.translate.failed')
            : t('inbox.translate.to', { language: t(`inbox.translate.lang.${to}`) })
      }
      side="top"
      onClick={() => run.mutate()}
      disabled={!text.trim() || run.isPending}
      data-testid="translate-draft"
    />
  );
}
